"use client";
import { useEffect, useRef, useState } from "react";

const WHEEL_MIN_INTERVAL_MS = 3000; // debounce between cycles
const WHEEL_THRESHOLD = 5000; // higher = less sensitive
const WHEEL_QUIET_RESET_MS = 3000; // time without events to end a gesture
import { useEditor, type EditorInstance } from "novel";

type EditorWithGhost = EditorInstance & {
  commands: {
    setGhostSuggestion?: (text: string) => unknown;
    clearGhostSuggestion?: () => unknown;
    setGhostVariants?: (variants: string[]) => unknown;
  };
};

export default function GhostBranching({ personaKey }: { personaKey: string }) {
  const { editor } = useEditor();
  const [ghost, setGhost] = useState("");
  const [variants, setVariants] = useState<string[] | null>(null);
  const [_variantIndex, setVariantIndex] = useState<number>(0);
  const variantsRef = useRef<string[] | null>(null);
  const variantIndexRef = useRef<number>(0);
  const timerRef = useRef<number | null>(null);
  const fetchRef = useRef<AbortController | null>(null);
  const streamingRef = useRef<boolean>(false);
  const fetchingVariantsKey = useRef<string | null>(null);
  const scrollLockTimerRef = useRef<number | null>(null);
  const wheelAccumRef = useRef<number>(0);
  const lastCycleTsRef = useRef<number>(0);
  const gestureActiveRef = useRef<boolean>(false);
  const gestureQuietTimerRef = useRef<number | null>(null);
  // listen for wheel events emitted directly by the ghost element

  // clear ghost when document changes significantly, and schedule idle trigger
  useEffect(() => {
    if (!editor) return;
    const handler = ({
      transaction,
    }: { transaction?: { docChanged?: boolean; getMeta?: (key: string) => unknown } }) => {
      if (transaction?.docChanged) {
        const text = transaction.getMeta?.("insertText") as string | undefined;
        if (text && ghost.startsWith(text)) {
          // user is typing same as ghost; trim prefix
          setGhost((g) => g.slice(text.length));
          (editor as unknown as EditorWithGhost)?.commands?.setGhostSuggestion?.(ghost.slice(text.length));
        } else {
          setGhost("");
          setVariants(null);
          (editor as unknown as EditorWithGhost)?.commands?.clearGhostSuggestion?.();
        }

        // Determine if current line has any content; if not, do not suggest
        try {
          const { from, to } = editor.state.selection;
          if (from === to) {
            const before = editor.state.doc.textBetween(0, from, "\n", " ") || "";
            const line = before.split("\n").pop() ?? "";
            if (!line.trim()) {
              if (timerRef.current) window.clearTimeout(timerRef.current);
              return; // no ghost on empty line
            }
          }
        } catch {}

        // schedule idle suggestion only for real content edits (docChanged)
        if (timerRef.current) window.clearTimeout(timerRef.current);
        timerRef.current = window.setTimeout(() => {
          void triggerSuggestion();
        }, 1200);
      }
    };
    editor.on("transaction", handler);
    return () => {
      editor.off?.("transaction", handler);
    };
  }, [editor, ghost]);

  // cancel timer on unmount
  useEffect(
    () => () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    },
    [],
  );

  useEffect(() => {
    variantsRef.current = variants;
  }, [variants]);
  useEffect(() => {
    variantIndexRef.current = _variantIndex;
  }, [_variantIndex]);

  async function triggerSuggestion() {
    if (!editor || streamingRef.current) return;
    let context = "";
    try {
      const { from, to } = editor.state.selection;
      if (from !== to) return; // not when selecting
      const before = editor.state.doc.textBetween(0, from, "\n", " ");
      context = (before ?? "").slice(-800);
      // Also ensure current line is not empty
      const line = (before ?? "").split("\n").pop() ?? "";
      if (!line.trim()) return;
    } catch {
      return;
    }

    fetchRef.current?.abort();
    const controller = new AbortController();
    fetchRef.current = controller;
    setGhost("");
    setVariants(null);
    (editor as unknown as EditorWithGhost)?.commands?.clearGhostSuggestion?.();
    setVariantIndex(0);

    try {
      streamingRef.current = true;
      const key = typeof window !== "undefined" ? window.localStorage.getItem("openai_api_key") || "" : "";
      const res = await fetch("/api/branch", {
        method: "POST",
        signal: controller.signal,
        headers: {
          "content-type": "application/json",
          ...(key ? { authorization: `Bearer ${key}`, "x-openai-key": key } : {}),
        },
        body: JSON.stringify({ context, mode: "primary", companionKey: personaKey }),
      });
      if (!res.ok) {
        if (res.status === 400 || res.status === 401) {
          const msg = "missing api key";
          setGhost(msg);
          (editor as unknown as EditorWithGhost)?.commands?.setGhostSuggestion?.(msg);
        }
        streamingRef.current = false;
        return;
      }
      if (!res.body) return;
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const text = buf.replace(/\s+/g, " ").trimStart();
        setGhost(text);
        (editor as unknown as EditorWithGhost)?.commands?.setGhostSuggestion?.(text);
        if ((!variantsRef.current || variantsRef.current.length === 0) && !fetchingVariantsKey.current) {
          void ensureVariants();
        }
      }
    } catch (_) {
      // ignore
    } finally {
      streamingRef.current = false;
    }
  }

  const lastVariantKey = useRef<string>("");
  async function ensureVariants(): Promise<string[] | null> {
    if (!editor) return;
    try {
      const { from } = editor.state.selection;
      const before = editor.state.doc.textBetween(0, from, "\n", " ");
      const line = (before ?? "").split("\n").pop() ?? "";
      if (!line.trim()) return null; // no variants on empty line
      const context = before.slice(-800);
      const keyRef = `${personaKey}|${context}`;
      if (
        (lastVariantKey.current === keyRef && variantsRef.current && variantsRef.current.length > 0) ||
        fetchingVariantsKey.current === keyRef
      )
        return variantsRef.current ?? null;
      fetchingVariantsKey.current = keyRef;
      const key = typeof window !== "undefined" ? window.localStorage.getItem("openai_api_key") || "" : "";
      const res = await fetch("/api/branch", {
        method: "POST",
        headers: {
          "content-type": "application/json",
          ...(key ? { authorization: `Bearer ${key}`, "x-openai-key": key } : {}),
        },
        body: JSON.stringify({ context, mode: "variants", companionKey: personaKey }),
      });
      if (!res.ok) {
        fetchingVariantsKey.current = null;
        return variantsRef.current ?? null;
      }
      const data = (await res.json()) as { variants: string[] };
      setVariants(data.variants);
      setVariantIndex(0);
      lastVariantKey.current = keyRef;
      fetchingVariantsKey.current = null;
      return data.variants;
    } catch (_) {
      // ignore
      fetchingVariantsKey.current = null;
      return variantsRef.current ?? null;
    }
  }

  // Provide variants to the plugin; plugin cycles on wheel directly
  useEffect(() => {
    if (!editor) return;
    if (variants && variants.length > 0)
      (editor as unknown as EditorWithGhost)?.commands?.setGhostVariants?.(variants);
  }, [editor, variants]);

  // Intercept wheel at the editor root in capture phase: block page scroll
  // and cycle variants immediately when pointer is over the ghost element.
  useEffect(() => {
    if (!editor) return;
    const dom = editor.view.dom as HTMLElement;
    const onWheelCapture = async (e: WheelEvent) => {
      const path = (e.composedPath?.() || []) as HTMLElement[];
      const isGhost = path.some((n) => n?.classList?.contains?.("ghost-suggestion"));
      const isInEditor = path.includes(dom);
      if (!e.shiftKey || !isInEditor) return;
      // Always prevent page scroll when Shift-scrolling inside the editor
      e.preventDefault();
      // Only cycle when the pointer is actually over the ghost element
      if (!isGhost) return;
      // Strong page-scroll lock for macOS trackpads
      try {
        document.body.classList.add("ghost-scroll-lock");
        if (scrollLockTimerRef.current) window.clearTimeout(scrollLockTimerRef.current);
        scrollLockTimerRef.current = window.setTimeout(() => {
          document.body.classList.remove("ghost-scroll-lock");
          scrollLockTimerRef.current = null;
        }, 250);
      } catch {}
      let list = variantsRef.current;
      if (!list || list.length === 0) list = await ensureVariants();
      if (!list || list.length === 0) return;
      const absY = Math.abs(e.deltaY);
      const absX = Math.abs(e.deltaX);
      const primary = absY >= absX ? e.deltaY : e.deltaX; // support Shift-horizontal
      // Accumulate deltas; one cycle per gesture (burst)
      const now = Date.now();
      if (gestureQuietTimerRef.current) window.clearTimeout(gestureQuietTimerRef.current);
      gestureQuietTimerRef.current = window.setTimeout(() => {
        gestureActiveRef.current = false;
        wheelAccumRef.current = 0;
        gestureQuietTimerRef.current = null;
      }, WHEEL_QUIET_RESET_MS);

      wheelAccumRef.current += primary;
      if (gestureActiveRef.current) return; // already cycled in this gesture
      if (now - lastCycleTsRef.current < WHEEL_MIN_INTERVAL_MS) return;
      if (Math.abs(wheelAccumRef.current) < WHEEL_THRESHOLD) return;
      const dir = wheelAccumRef.current > 0 ? 1 : -1;
      gestureActiveRef.current = true;
      lastCycleTsRef.current = now;

      const len = list.length;
      const next = (variantIndexRef.current + dir + len) % len;
      const text = list[next] ?? "";
      setVariantIndex(next);
      setGhost(text);
      (editor as unknown as EditorWithGhost)?.commands?.setGhostSuggestion?.(text);
      (editor as unknown as EditorWithGhost)?.commands?.setGhostVariants?.(list);
    };
    dom.addEventListener("wheel", onWheelCapture, { capture: true, passive: false });
    // Keyboard fallback for trackpads: Shift + ArrowUp/ArrowDown to cycle
    const onKeyDown = async (e: KeyboardEvent) => {
      if (!e.shiftKey) return;
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      // Only act when a ghost suggestion is present
      if (!variantsRef.current || variantsRef.current.length === 0) {
        const v = await ensureVariants();
        if (!v || v.length === 0) return;
      }
      e.preventDefault();
      const list = variantsRef.current ?? [];
      const dir = e.key === "ArrowDown" ? 1 : -1;
      const len = list.length;
      const next = (variantIndexRef.current + dir + len) % len;
      const text = list[next] ?? "";
      setVariantIndex(next);
      setGhost(text);
      (editor as unknown as EditorWithGhost)?.commands?.setGhostSuggestion?.(text);
      (editor as unknown as EditorWithGhost)?.commands?.setGhostVariants?.(list);
    };
    dom.addEventListener("keydown", onKeyDown, true);
    return () => {
      dom.removeEventListener("wheel", onWheelCapture, true);
      dom.removeEventListener("keydown", onKeyDown, true);
    };
  }, [editor]);

  return null;
}
